import { createAsyncThunk, createSlice, createAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { Status } from 'utils/customTypes';
import { SLICE_STATUS } from 'utils/constants';
import { RootState } from 'state/store';
import { requestsSelectors } from 'state/normalized/requests/requestsSlice';
import exportRequestsAPI from './ExportRequestsAPI';

interface ExportRequestsState {
  exportRequestsStatus: Status;
  exportRequestsByFormStatus: Status;
  selectAll: boolean;
}

/* ============================= INITIAL STATE ============================== */

const initialState: ExportRequestsState = {
  exportRequestsStatus: SLICE_STATUS.IDLE,
  exportRequestsByFormStatus: SLICE_STATUS.IDLE,
  selectAll: false,
};

/* ============================= ACTIONS ============================== */
export const setSelectAll = createAction<boolean>(
  'exportRequests/SET_SELECT_ALL'
);

/* ============================== REDUX THUNK =============================== */

export const exportRequests = createAsyncThunk(
  'exportRequests/EXPORT_REQUESTS',
  async (requestIds: string[], { getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const selectAll = state.exportRequests.selectAll;
    // CGP-221: with select-all the backend ignores requestIds and exports the
    // whole org, so it must receive the filters the visible list was built
    // with. Without select-all the explicit requestIds already scope the
    // export and no filters are sent.
    const filters = selectAll
      ? requestsSelectors.selectExportFilters(state)
      : undefined;
    try {
      return await exportRequestsAPI.exportRequests(
        requestIds,
        selectAll,
        filters
      );
    } catch (error) {
      // Forward the v2 API's `{ error }` body (e.g. 400 "There are no
      // requests to export" when the select-all filters match nothing) so the
      // caller's .unwrap() rejects with the real reason. Non-Axios errors
      // rethrow so the thunk serializes them with their message intact.
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data);
      }
      throw error;
    }
  }
);

export const exportRequestsByForm = createAsyncThunk(
  'exportRequests/EXPORT_REQUESTS_BY_FORM',
  async (requestIds: string[], { getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const selectAll = state.exportRequests.selectAll;
    const filters = selectAll
      ? requestsSelectors.selectExportFilters(state)
      : undefined;
    try {
      return await exportRequestsAPI.exportRequestsByForm(
        requestIds,
        selectAll,
        filters
      );
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data);
      }
      throw error;
    }
  }
);

/* ================================= REDUCER ================================ */

const exportRequestsSlice = createSlice({
  name: 'exportRequests',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(exportRequests.pending, (state) => {
        state.exportRequestsStatus = SLICE_STATUS.LOADING;
      })
      // no `throw` here: throwing inside the reducer aborts the Immer produce,
      // so FAILED never committed and the export menu item spun forever
      .addCase(exportRequests.rejected, (state) => {
        state.exportRequestsStatus = SLICE_STATUS.FAILED;
      })
      .addCase(exportRequests.fulfilled, (state, action) => {
        window.location.href = action.payload;
        state.exportRequestsStatus = SLICE_STATUS.SUCCESS;
      })
      .addCase(exportRequestsByForm.pending, (state) => {
        state.exportRequestsByFormStatus = SLICE_STATUS.LOADING;
      })
      .addCase(exportRequestsByForm.rejected, (state) => {
        state.exportRequestsByFormStatus = SLICE_STATUS.FAILED;
      })
      .addCase(exportRequestsByForm.fulfilled, (state, action) => {
        window.location.href = action.payload;
        state.exportRequestsByFormStatus = SLICE_STATUS.SUCCESS;
      })
      .addCase(setSelectAll, (state, action) => {
        state.selectAll = action.payload;
      });
  },
});

/* =============================== SELECTORS ================================ */

export const selectExportRequestsStatus = (state: RootState) =>
  state.exportRequests.exportRequestsStatus;

export const selectExportRequestsByFormStatus = (state: RootState) =>
  state.exportRequests.exportRequestsByFormStatus;

export const selectSelectAll = (state: RootState) =>
  state.exportRequests.selectAll;

export default exportRequestsSlice.reducer;
